<template>
  <div class="h3 mb-4">Инфраструктура</div>

  <div v-if="!isQuestionnaireSent" class="paragraph-s_h text-secondary">
    <span class="text-red">*</span> — поля обязательные для заполнения
  </div>

  <e-form v-if="!isQuestionnaireSent" ref="formRef" class="mt-10">
    <inputs-group>
      <e-input
        v-model:number-value="form.waterDistance"
        type="number"
        class="inputs-group__tiny-input"
        label="Расстояние до моря, м"
        :rules="[maxLength(6)]"
        :maska="'#'.repeat(7)"
      />
    </inputs-group>

    <inputs-group title="Пляжи" class="mt-10">
      <div class="w-100">
        <expand-card
          v-for="(beach, index) in form.beaches"
          :key="index"
          ref="beachesExpandCardRefs"
          :expanded="beach.expanded"
          :error="beachesCardsWithError.includes(index)"
          title="Пляж"
          class="mb-4"
        >
          <template #title-append>
            <published-entity-icon v-if="beach.questionnaireId !== questionnaire?.id" />
            <e-btn icon variant="ghost" size="S" @click.stop="removeEntity('beach', index)">
              <e-icon name="trash" />
            </e-btn>
          </template>

          <inputs-group>
            <e-input
              v-model:number-value="beach.beachDistance"
              type="number"
              class="inputs-group__tiny-input"
              label="Расстояние до пляжа, м"
              :rules="[maxLength(6)]"
              :maska="'#'.repeat(7)"
            />
          </inputs-group>

          <inputs-group title="Характеристики пляжа" class="mt-10">
            <combobox-with-list
              v-model="beach.beachType"
              label="Добавить"
              :options="dictionaries?.equipment.beachTypes || []"
            />
          </inputs-group>
          <inputs-group title="Оборудование на пляже" class="mt-10">
            <combobox-with-list
              v-model="beach.beachEquipment"
              label="Добавить"
              :options="dictionaries?.equipment.beachEquipment || []"
            />
          </inputs-group>
          <inputs-group title="Платное оборудование на пляже" class="mt-10">
            <combobox-with-list
              v-model="beach.paidBeachEquipment"
              label="Добавить"
              :options="dictionaries?.equipment.paidBeachEquipment || []"
            />
          </inputs-group>
          <inputs-group title="Досуг и развлечения на пляже" class="mt-10">
            <combobox-with-list
              v-model="beach.beachEntertainment"
              label="Добавить"
              :options="dictionaries?.equipment.beachEntertainments || []"
            />
          </inputs-group>
        </expand-card>
        <div>
          <e-btn variant="link" prepend-icon-src="plus" @click="addBeach">Добавить пляж</e-btn>
        </div>
      </div>
    </inputs-group>
    
    <inputs-group title="Бассейны" class="mt-10">
      <div class="w-100">
        <expand-card
          v-for="(pool, index) in form.pools"
          :key="index"
          ref="poolsExpandCardRefs"
          :expanded="pool.expanded"
          :error="poolsCardsWithError.includes(index)"
          title="Бассейн"
          class="mb-4"
        >
          <template #title-append>
            <published-entity-icon v-if="pool.questionnaireId !== questionnaire?.id" />
            <e-btn icon variant="ghost" size="S" @click.stop="removeEntity('pool', index)">
              <e-icon name="trash" />
            </e-btn>
          </template>

          <e-radio-group v-model="pool.isOpen" :items="isOpenOptions" inline />

          <inputs-group title="Бассейн работает" class="mt-10">
            <e-radio-group v-model="pool.isWork" :items="isWorkOptions" inline />
          </inputs-group>

          <inputs-group title="Характеристики бассейна" class="mt-10">
            <combobox-with-list
              v-model="pool.characteristics"
              label="Добавить"
              :options="dictionaries?.equipment.poolCharacteristics || []"
            />
          </inputs-group>

          <inputs-group title="Фотографии бассейна" class="mt-10">
            <template #subtitle>
              Загрузите фотографии или укажите ссылку на фотобанк
              <div class="mt-1">Если вы уже загрузили фотографии, нет необходимости их дублировать.</div>
            </template>

            <content-images-uploader
              :ref="addPoolUploaderRef"
              v-model="pool.images"
              v-model:images-link="pool.photobankUrl"
              v-model:attachments="pool.attachments"
              :content-section="ContentSection.Equipment"
              :section-object-name="pool.id"
              @uploaded="saveIfHasChanges"
            />
          </inputs-group>
        </expand-card>
      </div>
      <e-btn variant="link" prepend-icon-src="plus" @click="addPool">Добавить бассейн</e-btn>
    </inputs-group>

    <inputs-group title="Бани и сауны" class="mt-10">
      <div class="w-100">
        <expand-card
          v-for="(sauna, index) in form.bathhousesAndSaunas"
          :key="index"
          ref="bathhousesAndSaunasExpandCardRefs"
          :expanded="sauna.expanded"
          :error="bathhousesAndSaunasCardsWithError.includes(index)"
          title="Баня или сауна"
          class="mb-4"
        >
          <template #title-append>
            <published-entity-icon v-if="sauna.questionnaireId !== questionnaire?.id" />

            <e-btn icon variant="ghost" size="S" @click.stop="removeEntity('sauna', index)">
              <e-icon name="trash" />
            </e-btn>
          </template>

          <e-select v-model="sauna.type" label="Тип" :items="dictionaries?.equipment.saunaTypes || []" class="mb-4" />

          <inputs-group title="Оплата" required class="mt-10">
            <e-radio-group
              v-model="sauna.paymentOption"
              inline
              :items="saunaPaymentOptions || []"
              :rules="[required()]"
            />
          </inputs-group>

          <inputs-group title="Фотографии" class="mt-10">
            <template #subtitle>
              Загрузите фотографии или укажите ссылку на фотобанк
              <div class="mt-1">Если вы уже загрузили фотографии, нет необходимости их дублировать.</div>
            </template>

            <content-images-uploader
              :ref="addSaunaUploaderRef"
              v-model="sauna.images"
              v-model:images-link="sauna.photobankUrl"
              v-model:attachments="sauna.attachments"
              :content-section="ContentSection.Equipment"
              :section-object-name="sauna.id"
              @uploaded="saveIfHasChanges"
            />
          </inputs-group>
        </expand-card>
      </div>
      <e-btn variant="link" prepend-icon-src="plus" @click="addSauna">Добавить баню или сауну</e-btn>
    </inputs-group>

    <inputs-group title="Детские бассейны" class="mt-10">
      <combobox-with-list
        v-model="form.childrenPools"
        label="Добавить"
        :options="dictionaries?.equipment.childrenPools || []"
      />
    </inputs-group>
    <inputs-group title="Описание водного комплекса" class="mt-10">
      <template #subtitle>
        Если в Вашем отеле есть водный комплекс, аквапарк или другие водные объекты, дайте краткое описание
      </template>
      <e-textarea v-model="form.waterComplexDescription" label="Описание" :rules="[maxLength(250)]" />
    </inputs-group>
    <inputs-group title="Красота и здоровье" class="mt-10">
      <combobox-with-list
        v-model="form.beauty"
        label="Добавить"
        :options="dictionaries?.equipment.beautyInfrastructure || []"
      />
    </inputs-group>
    <inputs-group title="Инфраструктура для детей" class="mt-10">
      <combobox-with-list
        v-model="form.childrenInfrastructure"
        label="Добавить"
        :options="dictionaries?.equipment.childrenInfrastructure || []"
      />
    </inputs-group>
    <inputs-group title="Открытые площадки для занятий спортом" class="mt-10">
      <combobox-with-list
        v-model="form.openSportsFields"
        label="Добавить"
        :options="dictionaries?.equipment.outdoorAreas || []"
      />
    </inputs-group>
    <inputs-group title="Крытые залы" class="mt-10">
      <combobox-with-list
        v-model="form.coveredAreas"
        label="Добавить"
        :options="dictionaries?.equipment.indoorAreas || []"
      />
    </inputs-group>
    <inputs-group title="Открытые зимние площадки" class="mt-10">
      <combobox-with-list
        v-model="form.openWinterAreas"
        label="Добавить"
        :options="dictionaries?.equipment.openWinterPlaygrounds || []"
      />
    </inputs-group>
    <inputs-group title="Площадки для горнолыжного отдыха" class="mt-10">
      <combobox-with-list
        v-model="form.skiAreas"
        label="Добавить"
        :options="dictionaries?.equipment.skiResorts || []"
      />
    </inputs-group>
    <inputs-group title="Парковка" required class="mt-10">
      <combobox-with-list
        v-model="form.parking"
        label="Добавить"
        :rules="[required()]"
        :options="dictionaries?.equipment.parkingTypes || []"
      />
    </inputs-group>
    <inputs-group title="Номера для людей с ограниченными возможностями" required class="mt-10">
      <e-radio-group v-model="form.disabledRooms" :rules="[isDefined()]" :items="disabledRoomsOptions" inline />
    </inputs-group>
    <inputs-group title="Удобства для людей с ограниченными возможностями" class="mt-10">
      <combobox-with-list
        v-model="form.disabilityInfrastructure"
        label="Добавить"
        :options="dictionaries?.equipment.disabilityInfrastructure || []"
      />
    </inputs-group>
    <inputs-group title="Прочее" class="mt-10">
      <combobox-with-list
        v-model="form.other"
        label="Добавить"
        :options="dictionaries?.equipment.anotherInfrastructure || []"
      />
    </inputs-group>

    <inputs-group title="Фотографии инфраструктуры" class="mt-10">
      <template #subtitle>
        Загрузите фотографии или укажите ссылку на фотобанк
        <div class="mt-1">Если вы уже загрузили фотографии, нет необходимости их дублировать.</div>
      </template>

      <content-images-uploader
        ref="infrastructureUploaderRef"
        v-model="form.images"
        v-model:images-link="form.photobankUrl"
        v-model:attachments="form.attachments"
        :content-section="ContentSection.Equipment"
        @uploaded="saveIfHasChanges"
      />
    </inputs-group>

    <div class="d-flex justify-space-between mt-10">
      <e-btn variant="secondary" prepend-icon-src="arrowLeft" @click="back">Назад</e-btn>
      <e-btn variant="primary" append-icon-src="arrowRight" :loading="isLoading" @click="onSubmit(true)">Далее</e-btn>
    </div>
  </e-form>

  <div v-else class="mt-10">
    <div v-if="form.waterDistance" class="mb-10">
      <div class="d-flex flex-wrap col-gap-10 row-gap-4">
        <property-item title="Расстояние до моря, м">
          {{ form.waterDistance }}
        </property-item>
      </div>
    </div>

    <div class="paragraph-m_h font-600 mb-4">Пляжи</div>

    <expand-card v-for="(beach, index) in form.beaches" :key="index" title="Пляж" :expanded="true" class="mb-4">
      <div class="d-flex flex-column row-gap-10">
        <div v-if="beach.beachDistance">
          <div class="d-flex flex-wrap col-gap-10 row-gap-4">
            <property-item title="Расстояние до пляжа, м">
              {{ beach.beachDistance }}
            </property-item>
          </div>
        </div>

        <div v-if="beach.beachType.length">
          <div class="paragraph-m_h font-600 mb-2">Характеристики пляжа</div>

          <e-chip v-for="characteristic in beach.beachType" :key="characteristic" class="mt-2 mr-2">
            {{ characteristic }}
          </e-chip>
        </div>

        <div v-if="beach.beachEquipment.length">
          <div class="paragraph-m_h font-600 mb-2">Оборудование на пляже</div>
          <e-chip v-for="equipment in beach.beachEquipment" :key="equipment" class="mt-2 mr-2">
            {{ equipment }}
          </e-chip>
        </div>

        <div v-if="beach.paidBeachEquipment.length">
          <div class="paragraph-m_h font-600 mb-2">Платное оборудование на пляже</div>
          <e-chip v-for="payService in beach.paidBeachEquipment" :key="payService" class="mt-2 mr-2">
            {{ payService }}
          </e-chip>
        </div>

        <div v-if="beach.beachEntertainment.length">
          <div class="paragraph-m_h font-600 mb-2">Досуг и развлечения на пляже</div>
          <e-chip v-for="entertainment in beach.beachEntertainment" :key="entertainment" class="mt-2 mr-2">
            {{ entertainment }}
          </e-chip>
        </div>
      </div>
    </expand-card>

    <template v-if="form.pools.length">
      <div class="paragraph-m_h font-600 mt-10 mb-4">Бассейны</div>
      <expand-card v-for="(pool, index) in form.pools" :key="index" title="Бассейн" expanded class="mb-4">
        <div class="d-flex flex-column row-gap-10">
          <div v-if="pool.isOpen">
            <div class="paragraph-m_h font-600 mb-4">Тип</div>
            <e-radio-group v-if="pool.isOpen" v-model="pool.isOpen" disabled :items="isOpenOptions" inline />
          </div>

          <div v-if="pool.isWork">
            <div class="paragraph-m_h font-600 mb-4">Бассейн работает</div>
            <e-radio-group v-model="pool.isWork" disabled :items="isWorkOptions" inline class="mt-2 mr-2" />
          </div>

          <div v-if="pool.characteristics.length">
            <div class="paragraph-m_h font-600 mb-2">Характеристики бассейна</div>
            <e-chip v-for="characteristic in pool.characteristics" :key="characteristic" class="mt-2 mr-2">
              {{ characteristic }}
            </e-chip>
          </div>
        </div>

        <div v-if="pool.images.length || pool.photobankUrl" class="paragraph-m_h font-600 mt-10 mb-2">
          Фотографии бассейна
        </div>

        <images-gallery v-if="pool.images.length" class="mt-4" :files="pool.images" />

        <property-item v-if="pool.photobankUrl" title="Ссылка на фотобанк" class="mt-4">
          <a :href="pool.photobankUrl" target="_blank" class="link">{{ pool.photobankUrl }}</a>
        </property-item>
      </expand-card>
    </template>

    <template v-if="form.bathhousesAndSaunas.length">
      <div class="paragraph-m_h font-600 mt-10 mb-4">Бани и сауны</div>
      <expand-card
        v-for="(sauna, index) in form.bathhousesAndSaunas"
        :key="index"
        title="Баня или сауна"
        expanded
        class="mb-4"
      >
        <div class="d-flex flex-column row-gap-10">
          <template v-if="sauna.type">
            <property-item title="Тип">
              {{ sauna.type }}
            </property-item>
          </template>

          <div v-if="sauna.paymentOption">
            <div class="paragraph-m_h font-600 mb-4">Оплата</div>
            <e-radio-group
              v-model="sauna.paymentOption"
              disabled
              :items="saunaPaymentOptions || []"
              inline
              class="mt-2 mr-2"
            />
          </div>
        </div>

        <div v-if="sauna.images.length || sauna.photobankUrl" class="paragraph-m_h font-600 mt-10 mb-2">Фотографии</div>

        <images-gallery v-if="sauna.images.length" class="mt-4" :files="sauna.images" />

        <property-item v-if="sauna.photobankUrl" title="Ссылка на фотобанк" class="mt-4">
          <a :href="sauna.photobankUrl" target="_blank" class="link">{{ sauna.photobankUrl }}</a>
        </property-item>
      </expand-card>
    </template>

    <template v-if="form.childrenPools.length">
      <div class="paragraph-m_h font-600 mt-10 mb-2">Детские бассейны</div>
      <e-chip v-for="pool in form.childrenPools" :key="pool" class="mt-2 mr-2">
        {{ pool }}
      </e-chip>
    </template>

    <template v-if="form.waterComplexDescription">
      <div class="paragraph-m_h font-600 mt-10 mb-2">Описание водного комплекса</div>
      <property-item title="Описание" class="mt-4">
        {{ form.waterComplexDescription }}
      </property-item>
    </template>

    <template v-if="form.beauty.length">
      <div class="paragraph-m_h font-600 mt-10 mb-2">Красота и здоровье</div>
      <e-chip v-for="beauty in form.beauty" :key="beauty" class="mt-2 mr-2">
        {{ beauty }}
      </e-chip>
    </template>

    <template v-if="form.childrenInfrastructure.length">
      <div class="paragraph-m_h font-600 mt-10 mb-2">Инфраструктура для детей</div>
      <e-chip v-for="infrastructure in form.childrenInfrastructure" :key="infrastructure" class="mt-2 mr-2">
        {{ infrastructure }}
      </e-chip>
    </template>

    <template v-if="form.openSportsFields.length">
      <div class="paragraph-m_h font-600 mb-2 mt-10">Открытые площадки для занятий спортом</div>
      <e-chip v-for="openSportsField in form.openSportsFields" :key="openSportsField" class="mt-2 mr-2">
        {{ openSportsField }}
      </e-chip>
    </template>

    <template v-if="form.coveredAreas.length">
      <div class="paragraph-m_h font-600 mb-2 mt-10">Крытые залы</div>
      <e-chip v-for="coveredArea in form.coveredAreas" :key="coveredArea" class="mt-2 mr-2">
        {{ coveredArea }}
      </e-chip>
    </template>

    <template v-if="form.openWinterAreas.length">
      <div class="paragraph-m_h font-600 mb-2 mt-10">Открытые зимние площадки</div>
      <e-chip v-for="openWinterArea in form.openWinterAreas" :key="openWinterArea" class="mt-2 mr-2">
        {{ openWinterArea }}
      </e-chip>
    </template>

    <template v-if="form.skiAreas.length">
      <div class="paragraph-m_h font-600 mb-2 mt-10">Площадки для горнолыжного отдыха</div>
      <e-chip v-for="skiArea in form.skiAreas" :key="skiArea" class="mt-2 mr-2">
        {{ skiArea }}
      </e-chip>
    </template>

    <template v-if="form.parking.length">
      <div class="paragraph-m_h font-600 mt-10 mb-2">Парковка</div>
      <e-chip v-for="parking in form.parking" :key="parking" class="mt-2 mr-2">
        {{ parking }}
      </e-chip>
    </template>

    <div class="paragraph-m_h font-600 mt-10 mb-4">Номера для людей с ограниченными возможностями</div>
    <e-radio-group v-model="form.disabledRooms" disabled :items="disabledRoomsOptions" inline />

    <template v-if="form.disabilityInfrastructure.length">
      <div class="paragraph-m_h font-600 mt-10 mb-2">Удобства для людей с ограниченными возможностями</div>
      <e-chip
        v-for="disabilityInfrastructure in form.disabilityInfrastructure"
        :key="disabilityInfrastructure"
        class="mt-2 mr-2"
      >
        {{ disabilityInfrastructure }}
      </e-chip>
    </template>

    <template v-if="form.other.length">
      <div class="paragraph-m_h font-600 mt-10 mb-2">Прочее</div>
      <e-chip v-for="other in form.other" :key="other" class="mt-2 mr-2">
        {{ other }}
      </e-chip>
    </template>

    <div v-if="form.images.length || form.photobankUrl" class="paragraph-m_h font-600 mt-10 mb-2">
      Фотографии инфраструктуры
    </div>

    <images-gallery v-if="form.images.length" class="mt-4" :files="form.images" />

    <property-item v-if="form.photobankUrl" title="Ссылка на фотобанк" class="mt-4">
      <a :href="form.photobankUrl" target="_blank" class="link">{{ form.photobankUrl }}</a>
    </property-item>

    <div class="d-flex justify-space-between mt-10">
      <e-btn variant="secondary" prepend-icon-src="arrowLeft" @click="back">Назад</e-btn>
      <e-btn variant="primary" append-icon-src="arrowRight" :loading="isLoading" @click="nextStep">Далее</e-btn>
    </div>
  </div>
  <remove-entity-confirmation v-model="isRemoveEntityConfirmationOpen" :action="_removeEntity" />
</template>

<script setup lang="ts">
import { type ComponentPublicInstance, computed, onBeforeUnmount, onMounted, reactive, ref, toRef, watch } from 'vue';
import RemoveEntityConfirmation from './RemoveEntityConfirmation.vue';
import type { EFormType } from '@/core/types/components/components';
import type { LoadedFile } from '@/core/types/files';
import ComboboxWithList from '@/core/components/interface/ComboboxWithList.vue';
import ExpandCard from '@/core/components/interface/ExpandCard.vue';
import InputsGroup from '@/core/components/interface/InputsGroup.vue';
import PropertyItem from '@/core/components/interface/PropertyItem.vue';
import { EBtn, EChip, EForm, EIcon, EInput, ERadioGroup, ESelect, ETextarea } from '@/core/components/ui';
import ImagesGallery from '@/core/components/ui/files/filesGallery.vue';
import { elementErrorSelector } from '@/core/consts/components';
import { compareObjects } from '@/core/utils/objects/get-delta';
import { sleep } from '@/core/utils/sleep';
import { isDefined, maxLength, required } from '@/core/utils/validation';
import { QuestionnaireMasterStep } from '@/graphql/generated';
import ContentImagesUploader from '@/parts/content/components/ContentImagesUploader.vue';
import PublishedEntityIcon from '@/parts/content/components/PublishedEntityIcon.vue';
import { useContentStore } from '@/parts/content/store';
import { ContentSection } from '@/parts/content/types';
import { useContentImagesUpload } from '@/parts/content/utils';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

const { accommodationObject } = useAccommodationObjectsStore();

const {
  dictionaries,
  back,
  questionnaire,
  questionnaireAnswers,
  saveQuestionnaire,
  saveQuestionnaireDebounced,
  moveToNextStep,
  isQuestionnaireSent,
  nextStep,
  needValidate,
  completeValidation,
} = useContentStore();

const { initImages, deleteImage } = useContentImagesUpload(toRef(() => accommodationObject.value?.cid));

const beachesExpandCardRefs = ref<ComponentPublicInstance[]>([]);
const poolsExpandCardRefs = ref<ComponentPublicInstance[]>([]);
const bathhousesAndSaunasExpandCardRefs = ref<ComponentPublicInstance[]>([]);

const beachesCardsWithError = ref<number[]>([]);
const poolsCardsWithError = ref<number[]>([]);
const bathhousesAndSaunasCardsWithError = ref<number[]>([]);

const poolUploaderRefs = ref<any[]>([]);
const saunaUploaderRefs = ref<any[]>([]);
const infrastructureUploaderRef = ref<any>();

const formRef = ref<EFormType>();

const form = reactive({
  beaches:
    questionnaireAnswers.value?.infrastructureInfo?.beaches?.map((beach) => ({
      expanded: false,
      beachId: beach.beachId,
      beachType: beach.beachType ?? [],
      beachEquipment: beach.beachEquipment ?? [],
      paidBeachEquipment: beach.paidBeachEquipment ?? [],
      beachEntertainment: beach.beachEntertainment ?? [],
      questionnaireId: beach.questionnaireId,
      beachDistance: beach.beachDistanceMeters ?? null,
    })) || [],
  pools:
    questionnaireAnswers.value?.infrastructureInfo?.pools?.map((pool) => ({
      expanded: false,
      id: pool.poolId,
      isOpen: pool.poolType,
      isWork: pool.poolSeason,
      characteristics: pool.poolCharacteristics ?? [],
      attachments: pool.attachments ?? [],
      photobankUrl: pool.photobankUrl ?? null,
      images: [] as LoadedFile[],
      questionnaireId: pool.questionnaireId,
    })) || [],
  bathhousesAndSaunas:
    questionnaireAnswers.value?.infrastructureInfo?.saunas?.map((sauna) => ({
      expanded: false,
      id: sauna.saunaId,
      type: sauna.saunaType,
      paymentOption: sauna.payment,
      attachments: sauna.attachments ?? [],
      photobankUrl: sauna.photobankUrl ?? null,
      images: [] as LoadedFile[],
      questionnaireId: sauna.questionnaireId,
    })) || [],
  beauty: questionnaireAnswers.value?.infrastructureInfo?.beautyInfrastructure ?? [],
  waterComplexDescription: questionnaireAnswers.value?.infrastructureInfo?.waterComplexDescription ?? null,
  parking: questionnaireAnswers.value?.infrastructureInfo?.parkingType ?? [],
  disabledRooms: questionnaireAnswers.value?.infrastructureInfo?.disabilityCheck ?? null,
  disabilityInfrastructure: questionnaireAnswers.value?.infrastructureInfo?.disabilityInfrastructure ?? [],
  other: questionnaireAnswers.value?.infrastructureInfo?.anotherInfrastructure ?? [],
  childrenPools: questionnaireAnswers.value?.infrastructureInfo?.childrenPool ?? [],
  childrenInfrastructure: questionnaireAnswers.value?.infrastructureInfo?.childrenInfrastructure ?? [],
  openSportsFields: questionnaireAnswers.value?.infrastructureInfo?.outdoorAreas ?? [],
  coveredAreas: questionnaireAnswers.value?.infrastructureInfo?.indoorAreas ?? [],
  openWinterAreas: questionnaireAnswers.value?.infrastructureInfo?.winterPlaygrounds ?? [],
  skiAreas: questionnaireAnswers.value?.infrastructureInfo?.skiResorts ?? [],
  attachments: questionnaireAnswers.value?.infrastructureInfo?.attachments ?? [],
  photobankUrl: questionnaireAnswers.value?.infrastructureInfo?.photobankUrl ?? null,
  waterDistance: questionnaireAnswers.value?.infrastructureInfo?.waterDistanceMeters ?? null,
  images: [] as LoadedFile[],
});

const addPoolUploaderRef = (el: any) => {
  if (el && !poolUploaderRefs.value.includes(el)) {
    poolUploaderRefs.value.push(el);
  }
};

const addSaunaUploaderRef = (el: any) => {
  if (el && !saunaUploaderRefs.value.includes(el)) {
    saunaUploaderRefs.value.push(el);
  }
};

form.pools.forEach((pool, poolIndex: number) => {
  void initImages(pool.attachments, pool.images, ContentSection.Equipment, form.pools[poolIndex].id);
});
form.bathhousesAndSaunas.forEach((sauna, saunaIndex: number) => {
  void initImages(sauna.attachments, sauna.images, ContentSection.Equipment, form.bathhousesAndSaunas[saunaIndex].id);
});
void initImages(form.attachments, form.images, ContentSection.Equipment);

const addBeach = () => {
  form.beaches.push({
    expanded: true,
    beachId: crypto.randomUUID(),
    beachType: [],
    beachEquipment: [],
    paidBeachEquipment: [],
    beachEntertainment: [],
    questionnaireId: questionnaire.value?.id,
    beachDistance: null,
  });

  if (formRef.value?.isDirty) {
    void validate();
  }
};

const addPool = () => {
  form.pools.push({
    expanded: true,
    id: crypto.randomUUID(),
    isOpen: '',
    isWork: '',
    characteristics: [],
    attachments: [],
    photobankUrl: null,
    images: [] as LoadedFile[],
    questionnaireId: questionnaire.value?.id,
  });

  if (formRef.value?.isDirty) {
    void validate();
  }
};

const addSauna = () => {
  form.bathhousesAndSaunas.push({
    expanded: true,
    id: crypto.randomUUID(),
    paymentOption: '',
    type: '',
    attachments: [],
    photobankUrl: null,
    images: [] as LoadedFile[],
    questionnaireId: questionnaire.value?.id,
  });

  if (formRef.value?.isDirty) {
    void validate();
  }
};

const isRemoveEntityConfirmationOpen = ref(false);
const removingEntity = ref<'beach' | 'pool' | 'sauna'>();
const removingEntityIndex = ref<number>();

const _removeEntity = async () => {
  if (!removingEntity.value || removingEntityIndex.value === undefined) {
    return;
  }

  if (removingEntity.value === 'beach') {
    _removeBeach(removingEntityIndex.value);
  } else if (removingEntity.value === 'pool') {
    await _removePool(removingEntityIndex.value);
  } else if (removingEntity.value === 'sauna') {
    await _removeSauna(removingEntityIndex.value);
  }

  removingEntity.value = undefined;
  removingEntityIndex.value = undefined;
};

const removeEntity = (entity: 'beach' | 'pool' | 'sauna', index: number) => {
  removingEntity.value = entity;
  removingEntityIndex.value = index;

  const ent =
    entity === 'beach' ? form.beaches[index] : entity === 'pool' ? form.pools[index] : form.bathhousesAndSaunas[index];

  if (ent?.questionnaireId !== questionnaire.value?.id) {
    isRemoveEntityConfirmationOpen.value = true;
  } else {
    void _removeEntity();
  }
};

const _removeBeach = (index: number) => {
  form.beaches.splice(index, 1);
};

const _removePool = async (index: number) => {
  const poolToRemove = form.pools.splice(index, 1)[0];
  for (const file of poolToRemove.images) {
    await deleteImage(file.id, ContentSection.Equipment, poolToRemove.id, poolToRemove.attachments);
  }
  poolUploaderRefs.value = [];
};

const _removeSauna = async (index: number) => {
  const saunaToRemove = form.bathhousesAndSaunas.splice(index, 1)[0];
  for (const file of saunaToRemove.images) {
    await deleteImage(file.id, ContentSection.Equipment, saunaToRemove.id, saunaToRemove.attachments);
  }
  saunaUploaderRefs.value = [];
};

const save = () => saveQuestionnaireDebounced(mapAnswersToSave());
const saveIfHasChanges = async () => {
  if (hasChanges()) {
    await saveQuestionnaire(mapAnswersToSave());
  }
};

watch(
  form,
  () => {
    void save();
  },
  {
    deep: true,
  },
);

const isLoading = ref(false);

const validate = async () => {
  await sleep(0);
  
  beachesCardsWithError.value = [];
  poolsCardsWithError.value = [];
  bathhousesAndSaunasCardsWithError.value = [];

  const isFormValid = await formRef.value?.validate();

  beachesExpandCardRefs.value.forEach((card, index) => {
    const error = card.$el.querySelector(elementErrorSelector);
    if (error) {
      beachesCardsWithError.value.push(index);
    }
  });
  
  poolsExpandCardRefs.value.forEach((card, index) => {
    const error = card.$el.querySelector(elementErrorSelector);
    if (error) {
      poolsCardsWithError.value.push(index);
    }
  });
  
  bathhousesAndSaunasExpandCardRefs.value.forEach((card, index) => {
    const error = card.$el.querySelector(elementErrorSelector);
    if (error) {
      bathhousesAndSaunasCardsWithError.value.push(index);
    }
  });

  if (!isFormValid || 
      beachesCardsWithError.value.length > 0 || 
      poolsCardsWithError.value.length > 0 || 
      bathhousesAndSaunasCardsWithError.value.length > 0) {
    
    if (beachesCardsWithError.value.length > 0) {
      const firstErrorIndex = beachesCardsWithError.value[0];
      beachesExpandCardRefs.value[firstErrorIndex]?.$el?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    } else if (poolsCardsWithError.value.length > 0) {
      const firstErrorIndex = poolsCardsWithError.value[0];
      poolsExpandCardRefs.value[firstErrorIndex]?.$el?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest' 
      });
    } else if (bathhousesAndSaunasCardsWithError.value.length > 0) {
      const firstErrorIndex = bathhousesAndSaunasCardsWithError.value[0];
      bathhousesAndSaunasExpandCardRefs.value[firstErrorIndex]?.$el?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest' 
      });
    }
    
    return false;
  }

  if (infrastructureUploaderRef.value && !infrastructureUploaderRef.value.validate()) {
    return false;
  }
  
  for (const uploaderRef of poolUploaderRefs.value) {
    if (uploaderRef && !uploaderRef.validate()) {
      return false;
    }
  }
  
  for (const uploaderRef of saunaUploaderRefs.value) {
    if (uploaderRef && !uploaderRef.validate()) {
      return false;
    }
  }

  return true;
};

const hasChanges = () => {
  if (questionnaireAnswers.value?.infrastructureInfo) {
    delete questionnaireAnswers.value.infrastructureInfo.__typename;
    questionnaireAnswers.value.infrastructureInfo.beaches?.forEach((beach) => {
      delete beach.__typename;
    });
    questionnaireAnswers.value.infrastructureInfo.pools?.forEach((pool) => {
      delete pool.__typename;
    });
    questionnaireAnswers.value.infrastructureInfo.saunas?.forEach((sauna) => {
      delete sauna.__typename;
    });

    if (!compareObjects(mapAnswersToSave().infrastructureInfo, questionnaireAnswers.value.infrastructureInfo)) {
      return true;
    }
  }

  return false;
};

const onSubmit = async (force = false) => {
  if (!force && !hasChanges()) {
    return true;
  }

  const isValid = await validate();
  if (!isValid) {
    return false;
  }

  isLoading.value = true;
  await moveToNextStep(QuestionnaireMasterStep.InfrastructureInfo, mapAnswersToSave());
  isLoading.value = false;

  return true;
};

onMounted(async () => {
  if (needValidate.value) {
    await validate();
    void completeValidation();
  }
});

onBeforeUnmount(async () => {
  await saveIfHasChanges();
});

function mapAnswersToSave() {
  return {
    infrastructureInfo: {
      beaches: form.beaches.map((beach) => ({
        beachId: beach.beachId,
        beachType: beach.beachType,
        beachEquipment: beach.beachEquipment,
        paidBeachEquipment: beach.paidBeachEquipment,
        beachEntertainment: beach.beachEntertainment,
        beachDistanceMeters: beach.beachDistance,
      })),
      pools: form.pools.map((pool) => ({
        poolId: pool.id,
        poolType: pool.isOpen,
        poolSeason: pool.isWork,
        poolCharacteristics: pool.characteristics,
        attachments: pool.attachments,
        photobankUrl: pool.photobankUrl,
      })),
      saunas: form.bathhousesAndSaunas.map((sauna) => ({
        saunaId: sauna.id,
        saunaType: sauna.type,
        payment: sauna.paymentOption,
        attachments: sauna.attachments,
        photobankUrl: sauna.photobankUrl,
      })),
      anotherInfrastructure: form.other,
      beautyInfrastructure: form.beauty,
      childrenPool: form.childrenPools,
      childrenInfrastructure: form.childrenInfrastructure,
      disabilityCheck: form.disabledRooms,
      disabilityInfrastructure: form.disabilityInfrastructure,
      indoorAreas: form.coveredAreas,
      outdoorAreas: form.openSportsFields,
      parkingType: form.parking,
      skiResorts: form.skiAreas,
      waterComplexDescription: form.waterComplexDescription,
      winterPlaygrounds: form.openWinterAreas,
      attachments: form.attachments,
      photobankUrl: form.photobankUrl,
      waterDistanceMeters: form.waterDistance,
    },
  };
}

const disabledRoomsOptions = [
  {
    title: 'Есть',
    value: true,
  },
  {
    title: 'Нет',
    value: false,
  },
];

const isOpenOptions = [
  {
    title: 'Открытый',
    value: 'Открытый',
  },
  {
    title: 'Закрытый',
    value: 'Закрытый',
  },
];

const isWorkOptions = [
  {
    title: 'Круглогодично',
    value: 'Круглогодично',
  },
  {
    title: 'Сезонно',
    value: 'Сезонно',
  },
];

const saunaPaymentOptions = computed(() => {
  const paymentOptions = dictionaries?.value?.equipment.saunaPay.map((val) => ({
    title: val,
    value: val,
  }));

  return paymentOptions;
});
</script>
